import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { plans } from "../data/pricing";
import { SectionHead } from "./Services";
import MagneticButton from "./MagneticButton";
import { useLanguage } from "../context/LanguageContext";
// Prices are fixed in ILS; show raw values from data/pricing

export default function Pricing({ currency = "ILS" }) {
  const activeCurrency = currency || "ILS";
  const { t, dir, lang } = useLanguage();

  return (
    <section
      id="pricing"
      dir={dir}
      lang={lang}
      className="relative py-20 sm:py-24 md:py-32 px-5 sm:px-8"
      data-testid="pricing-section"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHead
          eyebrow={t.pricing.eyebrow}
          title={t.pricing.title}
          sub={t.pricing.sub}
        />

        <div className="mt-8 sm:mt-12 grid gap-6 md:grid-cols-3">
          {plans.map((p, i) => {
            const symbol = "$";
            // If a priceLabel includes a leading 'from', strip it so we don't repeat 'Starting from'
            const display = p.priceLabel
              ? p.priceLabel.replace(/^\s*[Ff]rom\s*/i, "").trim()
              : symbol + p.price.toLocaleString("en-US");
            const tp = t.pricing.plans[p.id];

            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`relative rounded-2xl border p-8 md:p-10 flex flex-col ${
                  p.featured
                    ? "trace-border bg-gradient-to-b from-agency-purple/30 via-black to-black border-agency-gold/30"
                    : "bg-agency-gray border-white/10 hover:border-agency-gold/40 transition-colors"
                }`}
                data-testid={`pricing-card-${p.id}`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-agency-gold text-black text-[10px] uppercase tracking-[0.3em] font-bold">
                    {t.pricing.mostPopular}
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-2xl font-bold">{tp.name}</h3>
                  <span className="font-mono-tech text-[11px] text-white/40">
                    0{i + 1}/03
                  </span>
                </div>
                <p className="mt-2 text-sm text-white/55">{tp.tagline}</p>

                <div className="mt-8 flex items-end gap-3 flex-nowrap">
                  <span className="pb-2 text-[10px] uppercase tracking-[0.28em] text-white/45">
                    {t.pricing.startingFrom}
                  </span>
                  <span className="font-display text-4xl sm:text-5xl md:text-6xl font-black gradient-gold-text leading-none whitespace-nowrap" dir="ltr">
                    {display}
                  </span>
                </div>
                <p className="mt-1 text-xs text-white/40">{tp.audience}</p>

                <ul className="mt-8 flex-1 space-y-3 text-sm text-white/80">
                  {tp.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check
                        size={16}
                        className="mt-0.5 flex-shrink-0 text-agency-gold"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between text-xs text-white/45">
                  <span className="uppercase tracking-[0.2em]">{t.pricing.deliveryLabel}</span>
                  <span className="text-white/85 font-mono-tech">{tp.delivery}</span>
                </div>

                <MagneticButton>
                  <Link
                    to="/contact"
                    className={`mt-6 inline-flex items-center justify-center gap-2 w-full px-6 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${
                      p.featured
                        ? "bg-agency-gold text-black hover:bg-white"
                        : "border border-white/20 text-white hover:border-agency-gold hover:text-agency-gold"
                    }`}
                    data-testid={`pricing-cta-${p.id}`}
                  >
                    {t.pricing.startWith(tp.name)}
                    <ArrowRight size={14} className="rtl:rotate-180" />
                  </Link>
                </MagneticButton>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs uppercase tracking-[0.25em] text-white/40">
          {t.pricing.note}
        </p>
        <p
          className="mt-4 text-center text-[11px] text-white/35"
          data-testid="pricing-payment-methods-note"
        >
          {t.pricing.paymentMethods}
        </p>
      </div>
    </section>
  );
}
